<template>
    <div class="flex flex-col gap-5 rounded-lgr border border-border bg-surface-raised p-6 pb-4 sm:p-10 sm:pb-7">
        <div class="flex flex-col gap-1">
            <h3 class="title-subsection text-ink">Sharpen your insights</h3>
            <p class="body-small text-ink-muted">Get more specific insights about the people you link.</p>
        </div>

        <div class="border-t border-border"></div>

        <!-- These counters intentionally use a one-off 24px scaffold style instead of adding a card-specific typography utility. -->
        <div class="grid grid-cols-2 gap-5">
            <div class="flex flex-col rounded-lgr bg-surface-sunken p-5">
                <span class="scaffold-body-bold text-2xl text-ink">{{ linkedCount }}</span>
                <span class="scaffold-details-small text-ink">{{ linkedCount === 1 ? "person linked" : "people linked" }}</span>
            </div>
            <div class="flex flex-col rounded-lgr bg-surface-sunken p-5">
                <span class="scaffold-body-bold text-2xl text-ink">
                    <SkeletonLoader v-if="isLoading" class="inline-block h-[0.7em] w-8 rounded-full align-middle" aria-label="Loading open suggestions" />
                    <template v-else>{{ openCount }}</template>
                </span>
                <span class="scaffold-details-small text-ink">{{ openCount === 1 ? "open suggestion" : "open suggestions" }}</span>
            </div>
        </div>

        <SkeletonLoader v-if="isLoading" class="h-10 w-full rounded-full" aria-label="Loading suggestions" />

        <div
            v-else-if="loadFailed"
            class="flex flex-col items-center gap-2 rounded-lg border border-border bg-surface-sunken px-4 py-3 text-center"
            data-test-id="profile-link-load-error">
            <p class="body-small text-ink-muted">Couldn't load suggestions right now.</p>
            <BaseButton theme="secondary" size="sm" data-test-id="profile-link-retry-btn" @click="reload">Try again</BaseButton>
        </div>

        <div v-else-if="showCelebration" class="flex items-center gap-3 rounded-lg border border-border px-4 py-3">
            <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-ink-inverse">
                <i class="bi bi-check-lg text-base/none icon-semibold" aria-hidden="true"></i>
            </span>
            <div>
                <p class="body-small text-ink-muted">
                    <span class="body-small-bold text-ink">All caught up!</span>
                </p>
                <p class="body-small text-ink-muted">New suggestions will appear here as they're available.</p>
            </div>
        </div>

        <BaseButton v-else theme="primary" data-test-id="review-suggestions-btn" @click="openDialog"> Review suggestions </BaseButton>

        <ProfileLinkSuggestionsDialog
            v-model:open="isDialogOpen"
            :suggestions="suggestions"
            :statuses="effectiveStatuses"
            :show-celebration="showCelebration"
            :own-profile-share="ownProfileShare"
            :has-collaboration-profile="hasCollaborationProfile"
            :org-has-ms-teams="orgHasMsTeams"
            :profile-link-sharing-enabled="profileLinkSharingEnabled"
            :can-link-all="canLinkAll"
            :is-linking-all="isLinkingAll"
            :surface="surface"
            @linked="handleLinked"
            @link-failed="handleLinkFailed"
            @shared="handleShared"
            @share-failed="handleShareFailed"
            @dismissed="handleDismissed"
            @dismiss-failed="handleDismissFailed"
            @link-all="handleLinkAll"
            @create-profile-shown="emit('create-profile-shown')"
            @create-profile-click="emit('create-profile-click')" />

        <BaseToast v-model:open="isToastOpen" :theme="toastTheme" :description="toastMessage" :duration="toastDuration" />
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, onMounted, ref, watch } from "vue";

import ProfileLinkSuggestionsDialog from "./ProfileLinkSuggestionsDialog.vue";
import type {
    BulkCollabProfileLinkRequest,
    BulkCollabProfileLinkResult,
    OwnProfileShare,
    ProfileLinkSuggestion,
    ProfileLinkSuggestionsResponse,
    ShareFailureReason,
    ShareTarget,
    SuggestionStatus,
} from "./profileLinkTypes";
import { MAX_SUGGESTIONS } from "./profileLinkTypes";

const props = defineProps<{
    initialLinkedCount: number;
    ownProfileShare: OwnProfileShare | null;
    orgHasMsTeams: boolean;
    profileLinkSharingEnabled: boolean;
    surface: string;
}>();

const emit = defineEmits<{
    "create-profile-shown": [];
    "create-profile-click": [];
}>();

const {
    execute: fetchSuggestions,
    data: suggestionsData,
    error: loadError,
    isFetching: isLoading,
    isFinished: hasLoaded,
} = useFetch("/v1/connections/profile-link-suggestions", { immediate: false }).get().json<ProfileLinkSuggestionsResponse>();

const bulkLinkPayload = ref<BulkCollabProfileLinkRequest>();
const {
    data: bulkLinkData,
    error: bulkLinkError,
    statusCode: bulkLinkStatus,
    isFetching: isBulkLinking,
    execute: executeBulkLink,
} = useFetch("/v1/collaboration-profiles/bulk-ensure-link", { immediate: false }).post(bulkLinkPayload).json<BulkCollabProfileLinkResult>();

const statuses = ref<Record<string, SuggestionStatus>>({});
const dismissedIds = ref<Set<string>>(new Set());
const linkedDelta = ref(0);

const LINK_ALL_STAGGER_MS = 60;
const LINK_ALL_CELEBRATION_BEAT_MS = 450;

const LINK_ALL_PARTIAL_FAILURE_MESSAGE = "We had a problem linking all. Please manually link for now.";

const canLinkAll = computed(() => {
    const loaded = suggestionsData.value?.suggestions ?? [];
    return loaded.length === MAX_SUGGESTIONS && loaded.every((s) => s.action === "link");
});

const isSettlingLinkAll = ref(false);
const isLinkingAll = computed(() => isBulkLinking.value || isSettlingLinkAll.value);

const suggestions = computed(() => (suggestionsData.value?.suggestions ?? []).filter((s) => !dismissedIds.value.has(s.target_user_id)));
const linkedCount = computed(() => (suggestionsData.value?.linked_count ?? props.initialLinkedCount) + linkedDelta.value);

const effectiveStatuses = computed<Record<string, SuggestionStatus>>(() =>
    Object.fromEntries(suggestions.value.map((s) => [s.target_user_id, statuses.value[s.target_user_id] ?? "idle"])),
);

const openCount = computed(() => suggestions.value.filter((s) => effectiveStatuses.value[s.target_user_id] === "idle").length);
const hasCollaborationProfile = computed(() => props.ownProfileShare !== null);
const loadFailed = computed(() => hasLoaded.value && (loadError.value !== null || suggestionsData.value == null));
// `!isLinkingAll` holds the celebration back until the staggered pills have all landed —
// without it the last row's pill would be replaced by the success screen in the same frame.
const showCelebration = computed(() => hasLoaded.value && !loadFailed.value && openCount.value === 0 && !isLinkingAll.value);

const isDialogOpen = ref(false);

const isToastOpen = ref(false);
const toastMessage = ref("");
const toastTheme = ref<"primary" | "error">("primary");
const toastDuration = ref(5000);
const showToast = (message: string, theme: "primary" | "error" = "primary", duration = 5000) => {
    toastMessage.value = message;
    toastTheme.value = theme;
    toastDuration.value = duration;
    isToastOpen.value = true;
};

async function reload() {
    await fetchSuggestions();
}

onMounted(async () => {
    await fetchSuggestions();
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_CARD_SHOWN, {
        linked_count: linkedCount.value,
        suggestion_count: openCount.value,
        surface: props.surface,
    });
});

// The all-caught-up state is a strong activation signal. Fire once per batch clear.
const celebrationLogged = ref(false);
watch(showCelebration, (visible) => {
    if (visible && !celebrationLogged.value) {
        celebrationLogged.value = true;
        logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_CELEBRATION_SHOWN, {
            linked_count: linkedCount.value,
            surface: props.surface,
        });
    }
});

function openDialog() {
    isDialogOpen.value = true;
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_SEE_ALL_CLICKED, { suggestion_count: openCount.value, surface: props.surface });
}

function markLinked(targetUserId: string) {
    if (statuses.value[targetUserId] === "linked") {
        return;
    }
    statuses.value[targetUserId] = "linked";
    linkedDelta.value += 1;
}

function handleLinked(suggestion: ProfileLinkSuggestion) {
    markLinked(suggestion.target_user_id);
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINKED, { source: suggestion.source, surface: props.surface });
}

function handleLinkFailed(suggestion: ProfileLinkSuggestion) {
    showToast("Couldn't link that profile. Please try again.", "error");
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINK_FAILED, { source: suggestion.source, surface: props.surface });
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function handleLinkAll() {
    if (isLinkingAll.value) {
        return;
    }
    const batch = suggestions.value.filter((s) => s.action === "link" && effectiveStatuses.value[s.target_user_id] === "idle");
    if (batch.length === 0) {
        return;
    }

    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINK_ALL_CLICKED, { count: batch.length, surface: props.surface });

    const connectionIdsByTargetUserId = batch.reduce<Record<string, string>>((connectionIds, suggestion) => {
        if (suggestion.connection_id) {
            connectionIds[suggestion.target_user_id] = suggestion.connection_id;
        }
        return connectionIds;
    }, {});
    bulkLinkPayload.value = {
        target_user_ids: batch.map((s) => s.target_user_id),
        connection_ids_by_target_user_id: connectionIdsByTargetUserId,
    };
    await executeBulkLink();

    const result = bulkLinkError.value || bulkLinkStatus.value === null ? null : bulkLinkData.value;
    if (!result) {
        showToast(LINK_ALL_PARTIAL_FAILURE_MESSAGE, "error");
        return;
    }

    isSettlingLinkAll.value = true;
    try {
        for (const [index, targetUserId] of result.succeeded.entries()) {
            if (index > 0) {
                await wait(LINK_ALL_STAGGER_MS);
            }
            markLinked(targetUserId);
        }

        // Failed rows keep their Link button, so openCount stays above zero and the celebration
        // is withheld on its own — the toast is the only extra signal the user gets.
        if (result.failed.length > 0) {
            showToast(LINK_ALL_PARTIAL_FAILURE_MESSAGE, "error");
            return;
        }

        await wait(LINK_ALL_CELEBRATION_BEAT_MS);
    } finally {
        isSettlingLinkAll.value = false;
    }
}

function handleShared(suggestion: ProfileLinkSuggestion, target: ShareTarget) {
    statuses.value[suggestion.target_user_id] = "shared";
    if (target === "link") {
        showToast(`Copied! Drop it in a message to ${firstNameOf(suggestion.name)} so they can see how you work best.`, "primary", 10000);
    }
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_SHARED, { source: suggestion.source, target, surface: props.surface });
}

const SHARE_FAILURE_MESSAGES: Record<ShareFailureReason, string> = {
    not_publishable: "Publish your Collaboration Profile before sharing it.",
    invite: "Couldn't prepare the invite. Please try again.",
    clipboard: "Couldn't copy the invite. Please try again.",
};

function handleShareFailed(suggestion: ProfileLinkSuggestion, reason: ShareFailureReason) {
    showToast(SHARE_FAILURE_MESSAGES[reason], "error");
    // An unpublished profile is a setup step the user has to complete, not a failed request.
    if (reason !== "not_publishable") {
        logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_REQUEST_FAILED, { source: suggestion.source, surface: props.surface, reason });
    }
}

function handleDismissed(suggestion: ProfileLinkSuggestion) {
    dismissedIds.value.add(suggestion.target_user_id);
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_REMOVED, { source: suggestion.source, surface: props.surface });
}

function handleDismissFailed() {
    showToast("Couldn't dismiss that suggestion. Please try again.", "error");
}
</script>
